/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import {
    Briefcase,
    Building2,
    Calendar,
    Check,
    ChevronDown,
    Copy,
    Link2,
    LocateFixed,
    MousePointerClick,
    Search,
    SearchX,
    Settings2,
    ToggleRight,
    Type,
    Unlink,
    User,
    X,
} from "lucide-react";
import {
    CLONE_FIELD_PREFIX,
    clientFieldsConfig,
    getClientFieldShortLabel,
    getOriginLabel,
} from "./utils";
import { OVERFLOW_POLICY_OPTIONS, getOverflowPolicy } from "components/Forms/utils/overflowPolicy";

const ALL = "all";

const originIcons = {
    Attorney: Briefcase,
    Client: User,
    DocIssuer: Building2,
    Form: Copy,
    System: Settings2,
};

const typeMeta = {
    text: { label: "Text", Icon: Type },
    date: { label: "Date", Icon: Calendar },
    checkbox: { label: "Boolean", Icon: ToggleRight },
};

const compareText = (a, b) => a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

const normalize = (text) =>
    String(text)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

// Static, pre-sorted catalog: origins alphabetically, fields alphabetically within each origin
const catalog = clientFieldsConfig
    .map((field) => {
        const shortLabel = getClientFieldShortLabel(field);
        const originLabel = getOriginLabel(field.origin);
        const type = typeMeta[field.type] || typeMeta.text;
        return {
            ...field,
            shortLabel,
            originLabel,
            type,
            haystack: normalize([shortLabel, originLabel, field.label, field.id, type.label, field.description].join(" ")),
        };
    })
    .sort((a, b) => compareText(a.originLabel, b.originLabel) || compareText(a.shortLabel, b.shortLabel));

const origins = [...new Set(catalog.map((x) => x.origin))];

const getMappingOrigin = (value) => {
    if (!value) return null;
    if (value.startsWith(CLONE_FIELD_PREFIX)) return "Form";
    return catalog.find((x) => x.id === value)?.origin || null;
};

// Unmapped field: every origin collapsed. Mapped field: only the origin of its mapping expanded
const getInitialCollapsed = (value) => {
    const mappingOrigin = getMappingOrigin(value);
    return origins.reduce((acc, origin) => {
        acc[origin] = origin !== mappingOrigin;
        return acc;
    }, {});
};

const OriginIcon = ({ origin, size = 16 }) => {
    const Icon = originIcons[origin] || Link2;
    return (
        <span className={clsx("fmp-origin-icon", `origin-${origin}`)}>
            <Icon size={size} strokeWidth={2} />
        </span>
    );
};

const Highlight = ({ text, tokens }) => {
    if (!tokens.length) return text;
    const normalized = normalize(text);
    const marks = new Array(text.length).fill(false);
    tokens.forEach((token) => {
        let idx = normalized.indexOf(token);
        while (idx !== -1) {
            for (let i = idx; i < idx + token.length; i++) marks[i] = true;
            idx = normalized.indexOf(token, idx + token.length);
        }
    });
    const parts = [];
    let start = 0;
    for (let i = 1; i <= text.length; i++) {
        if (i === text.length || marks[i] !== marks[start]) {
            const chunk = text.slice(start, i);
            parts.push(marks[start] ? <mark key={start}>{chunk}</mark> : chunk);
            start = i;
        }
    }
    return parts;
};

const CurrentMapping = ({ value, onRemove, onLocate }) => {
    if (value.startsWith(CLONE_FIELD_PREFIX)) {
        const sourceField = value.replace(CLONE_FIELD_PREFIX, "");
        return (
            <div className="fmp-current">
                <OriginIcon origin="Form" />
                <div className="fmp-current__text">
                    <span className="fmp-current__caption">Cloned from PDF field</span>
                    <span className="fmp-current__label mono" title={sourceField}>
                        {sourceField}
                    </span>
                </div>
                <button type="button" className="fmp-icon-btn" onClick={() => onLocate(sourceField)} title="Locate source field">
                    <LocateFixed size={16} />
                </button>
                <button type="button" className="fmp-icon-btn danger" onClick={onRemove} title="Remove association">
                    <Unlink size={16} />
                </button>
            </div>
        );
    }

    const config = catalog.find((x) => x.id === value);
    return (
        <div className="fmp-current">
            <OriginIcon origin={config?.origin} />
            <div className="fmp-current__text">
                <span className="fmp-current__caption">{config ? config.originLabel : "Custom value"}</span>
                <span className="fmp-current__label" title={config?.label || value}>
                    {config ? config.shortLabel : value}
                </span>
            </div>
            <button type="button" className="fmp-icon-btn danger" onClick={onRemove} title="Remove association">
                <Unlink size={16} />
            </button>
        </div>
    );
};

/** What happens to a value longer than the PDF field's character limit (see overflowPolicy) */
const OverflowPolicySetting = ({ maxLength, policy, onChange }) => {
    const selected = getOverflowPolicy(policy);
    const option = OVERFLOW_POLICY_OPTIONS.find((x) => x.value === selected);
    return (
        <div className="fmp-limit">
            <span className="fmp-limit__caption">Character limit: {maxLength}</span>
            <label className="fmp-limit__field">
                <span>When the value is longer</span>
                <select value={selected} onChange={(e) => onChange(e.target.value)}>
                    {OVERFLOW_POLICY_OPTIONS.map((x) => (
                        <option key={x.value} value={x.value}>
                            {x.label}
                        </option>
                    ))}
                </select>
            </label>
            <p className="fmp-limit__hint">{option.description}</p>
        </div>
    );
};

const FieldMappingPanel = ({
    pdfFieldName,
    value,
    maxLength,
    overflowPolicy,
    onOverflowPolicyChange,
    usage,
    isCloning,
    onSelect,
    onRemove,
    onLocate,
    onCancelCloning,
    onClose,
}) => {
    const [query, setQuery] = useState("");
    const [originFilter, setOriginFilter] = useState(ALL);
    const [unusedOnly, setUnusedOnly] = useState(false);
    const [collapsed, setCollapsed] = useState(() => getInitialCollapsed(value));
    const searchRef = useRef(null);

    const tokens = useMemo(() => normalize(query).split(/\s+/).filter(Boolean), [query]);
    const isSearching = tokens.length > 0;

    // New PDF field selected: start with a fresh search, keep the filters
    useEffect(() => {
        setQuery("");
        setCollapsed(getInitialCollapsed(value));
        if (!isCloning) searchRef.current?.focus({ preventScroll: true });
    }, [pdfFieldName]);

    useEffect(() => {
        if (!isCloning) searchRef.current?.focus({ preventScroll: true });
    }, [isCloning]);

    // Fields matching search + "unused only" (origin filter applied later, so chips can show counts)
    const matching = useMemo(
        () =>
            catalog.filter((field) => {
                if (unusedOnly && usage[field.id] && field.id !== value) return false;
                return tokens.every((token) => field.haystack.includes(token));
            }),
        [tokens, unusedOnly, usage, value],
    );

    const originCounts = useMemo(
        () =>
            matching.reduce((acc, field) => {
                acc[field.origin] = (acc[field.origin] || 0) + 1;
                return acc;
            }, {}),
        [matching],
    );

    const groups = useMemo(() => {
        const visible = originFilter === ALL ? matching : matching.filter((x) => x.origin === originFilter);
        return origins
            .map((origin) => ({ origin, fields: visible.filter((x) => x.origin === origin) }))
            .filter((group) => group.fields.length > 0);
    }, [matching, originFilter]);

    const isGroupOpen = (origin) => isSearching || originFilter !== ALL || !collapsed[origin];

    const clearFilters = () => {
        setQuery("");
        setOriginFilter(ALL);
        setUnusedOnly(false);
        searchRef.current?.focus();
    };

    // Rendered in <body> so it spans the full viewport height regardless of the PDF container
    return createPortal(
        <aside className="field-mapping-panel" aria-label="Map PDF field">
            <header className="fmp-header">
                <div className="fmp-header__top">
                    <h3 className="fmp-header__title">Map PDF field</h3>
                    <button type="button" className="fmp-icon-btn" onClick={onClose} title="Close">
                        <X size={18} />
                    </button>
                </div>
                {value && !isCloning && <CurrentMapping value={value} onRemove={onRemove} onLocate={onLocate} />}
                {maxLength > 0 && !isCloning && (
                    <OverflowPolicySetting maxLength={maxLength} policy={overflowPolicy} onChange={onOverflowPolicyChange} />
                )}
            </header>

            {isCloning ? (
                <div className="fmp-cloning">
                    <div className="fmp-cloning__icon">
                        <MousePointerClick size={28} />
                    </div>
                    <h4>Pick the source field</h4>
                    <p>
                        Click the field on the PDF whose value should be copied into <strong className="mono">{pdfFieldName}</strong>.
                    </p>
                    <button type="button" className="fmp-btn" onClick={onCancelCloning}>
                        Cancel
                    </button>
                </div>
            ) : (
                <>
                    <div className="fmp-controls">
                        <label className="fmp-search">
                            <Search size={16} />
                            <input
                                ref={searchRef}
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={value ? "Search to replace mapping…" : "Search fields…"}
                                autoComplete="off"
                                spellCheck={false}
                                // The panel lives inside the Formik form: Enter must not submit it
                                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                            />
                            {query && (
                                <button type="button" className="fmp-icon-btn small" onClick={() => setQuery("")} title="Clear search">
                                    <X size={14} />
                                </button>
                            )}
                        </label>
                        <div className="fmp-chips" role="tablist" aria-label="Filter by origin">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={originFilter === ALL}
                                className={clsx("fmp-chip", { active: originFilter === ALL })}
                                onClick={() => setOriginFilter(ALL)}
                            >
                                All <span className="fmp-chip__count">{matching.length}</span>
                            </button>
                            {origins.map((origin) => (
                                <button
                                    key={origin}
                                    type="button"
                                    role="tab"
                                    aria-selected={originFilter === origin}
                                    className={clsx("fmp-chip", `origin-${origin}`, { active: originFilter === origin, empty: !originCounts[origin] })}
                                    onClick={() => setOriginFilter(originFilter === origin ? ALL : origin)}
                                >
                                    {getOriginLabel(origin)} <span className="fmp-chip__count">{originCounts[origin] || 0}</span>
                                </button>
                            ))}
                        </div>
                        <label className="fmp-toggle">
                            <input type="checkbox" checked={unusedOnly} onChange={(e) => setUnusedOnly(e.target.checked)} />
                            <span className="fmp-toggle__track" aria-hidden="true" />
                            <span>Only fields not used yet</span>
                        </label>
                    </div>

                    <div className="fmp-list" role="listbox">
                        {groups.length === 0 ? (
                            <div className="fmp-empty">
                                <SearchX size={28} />
                                <p>{query ? <>No fields match &ldquo;{query}&rdquo;</> : "No fields match the current filters"}</p>
                                <button type="button" className="fmp-btn" onClick={clearFilters}>
                                    Clear filters
                                </button>
                            </div>
                        ) : (
                            groups.map(({ origin, fields }) => {
                                const open = isGroupOpen(origin);
                                return (
                                    <section key={origin} className="fmp-group" role="group" aria-label={getOriginLabel(origin)}>
                                        <button
                                            type="button"
                                            className="fmp-group__header"
                                            onClick={() => setCollapsed((prev) => ({ ...prev, [origin]: !prev[origin] }))}
                                            disabled={isSearching || originFilter !== ALL}
                                            aria-expanded={open}
                                        >
                                            <ChevronDown size={14} className={clsx("fmp-chevron", { closed: !open })} />
                                            <span>{getOriginLabel(origin)}</span>
                                            <span className="fmp-group__count">{fields.length}</span>
                                        </button>
                                        {open &&
                                            fields.map((field) => {
                                                const isCurrent = field.id === value;
                                                const usedCount = usage[field.id] || 0;
                                                const TypeIcon = field.type.Icon;
                                                return (
                                                    <div
                                                        key={field.id}
                                                        role="option"
                                                        aria-selected={isCurrent}
                                                        className={clsx("fmp-option", { current: isCurrent })}
                                                        onClick={() => onSelect(field.id)}
                                                        title={field.label}
                                                    >
                                                        <OriginIcon origin={field.origin} size={15} />
                                                        <div className="fmp-option__text">
                                                            <span className="fmp-option__label">
                                                                <Highlight text={field.shortLabel} tokens={tokens} />
                                                            </span>
                                                            <span className="fmp-option__meta">
                                                                {field.description || (
                                                                    <>
                                                                        <TypeIcon size={12} /> {field.type.label}
                                                                    </>
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="fmp-option__aside">
                                                            {isCurrent ? (
                                                                <span className="fmp-badge current">
                                                                    <Check size={12} strokeWidth={3} /> Current
                                                                </span>
                                                            ) : (
                                                                usedCount > 0 && (
                                                                    <span className="fmp-badge used" title={`Already mapped to ${usedCount} other field(s)`}>
                                                                        Used ×{usedCount}
                                                                    </span>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </section>
                                );
                            })
                        )}
                    </div>

                </>
            )}
        </aside>,
        document.body,
    );
};

export default FieldMappingPanel;
